import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { cloneDeep } from 'lodash'
import { Col, Row, Space, Switch } from 'antd'
import { PrimaryButton } from 'Components/button'
import { isFormRequired, isFormError, formValidation, getMenuSelect } from 'helpers/format'
import {
  CreateContentReq,
  ContentDetailReq,
  ContentValue,
  ContentData,
  ContentDetailSection,
} from 'Store/manage-content/types'
import { createContentAPI, getContentDetailAPI } from 'Store/manage-content/api'
import {
  selectContentDetail,
  selectContentDetailEvent,
  selectContentActionEvent,
  selectPagenameList,
} from 'Store/manage-content/selector'
import { selectCurrentUser } from 'Store/user/selector'
import { LabelItem, AppInput, AppSelect, UploadForm } from 'Components/form'
import EventModal from 'Components/modal/EventModal'
import { clearEventAction, resetDetailAction } from 'Store/manage-content/actions'
import LoadingView from 'Components/modal/LoadingView'
import { DetailDrawer } from 'Components/common/MainLayout'
import styled from 'styled-components'
import { selectDatas } from 'Store/config/selector'
import { MyTheme } from 'Components/common/Theme'
import ContentForm from './ContentForm'
import { appConfig } from 'config'

type Props = ReduxProps & {
  visible: boolean
  onCloseDrawer: () => void
  onSuccess?: () => void
  mode: any
  pagename?: string
}

const ContentDetail: React.FunctionComponent<Props> = props => {
  const {
    onCloseDrawer,
    visible,
    currentUser,
    event,
    onSuccess = () => {},
    pagename = '',
    mode,
    detail,
    detailEvent,
    pagenamelist,
  } = props
  const isCreateMode = mode === 'create'
  const requireFeild = ['pagename']
  const initForm = {
    pagename: '',
    header: {
      alt: '',
      body: '',
      image: '',
      title: '',
    },
    footer: {
      alt: '',
      body: '',
      image: '',
      title: '',
    },
    status: 'active',
  } as CreateContentReq

  const [formData, setFormData] = useState(cloneDeep(initForm))
  const [currentPageName, setCurrentPageName] = useState('')
  const [formError, setFormError] = useState([] as string[])
  const dispatch = useDispatch()

  const validateForm = () => {
    const error = formValidation(requireFeild, formData)
    setFormError([...error])
    return error.length > 0 ? false : true
  }

  const onCreate = (): void => {
    if (!validateForm()) return
    const req: CreateContentReq = {
      ...formData,
    }
    dispatch(createContentAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onGetDetail = (pagename: string): void => {
    if (!pagename) {
      dispatch(resetDetailAction())
      return
    }
    const req: ContentDetailReq = {
      pagename,
    }
    dispatch(getContentDetailAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  useEffect(() => {
    // clear form data when user cloase drawer
    if (!visible) {
      setFormData(cloneDeep(initForm))
      setFormError([])
      setCurrentPageName('')
    }
  }, [visible])

  useEffect(() => {
    if (visible) {
      setCurrentPageName(pagename)
    }
  }, [visible, pagename])

  useEffect(() => {
    if (detail) {
      const footer = detail?.sections?.find((item: ContentDetailSection) => item.section_name === 'Footer')
      const header = detail?.sections?.find((item: ContentDetailSection) => item.section_name === 'Header')
      const form = {
        pagename: detail?.pagename,
        status: detail?.status,
        footer: {
          alt: footer?.alt || '',
          body: footer?.body || '',
          image: footer?.image || '',
          title: footer?.title || '',
        },
        header: {
          alt: header?.alt || '',
          body: header?.body || '',
          image: header?.image || '',
          title: header?.title || '',
        },
      } as CreateContentReq
      setFormData({ ...form })
    }
  }, [detail])

  const resetData = () => {
    setFormData({
      ...cloneDeep(initForm),
      pagename: currentPageName,
    })
  }

  useEffect(() => {
    if (visible) {
      resetData()
      onGetDetail(currentPageName)
    }
  }, [currentPageName, mode])

  useEffect(() => {
    if (formError.length > 0) {
      validateForm()
    }
  }, [formData])

  const onUpdateData = (val: any, key: string) => {
    setFormData({
      ...formData,
      [key]: val,
    })
  }

  const handleToggleStatus = (val: boolean) => {
    setFormData({
      ...formData,
      status: val ? 'active' : 'inactive',
    })
  }

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    clearEvent()
    onSuccess()
  }

  const isRequired = (name: string) => {
    return isFormRequired(requireFeild, name)
  }

  const isError = (name: string) => {
    return isFormError(formError, name)
  }

  const title = isCreateMode ? 'สร้าง Content' : 'แก้ไข Content'

  const FooterContent = (
    <Space size="middle" align="baseline">
      <PrimaryButton text="ยกเลิก" btnType="Cancel" onClick={onCloseDrawer} />
      <PrimaryButton text="บันทึกข้อมูล" onClick={onCreate} />
    </Space>
  )

  return (
    <DetailDrawer
      title={title}
      onCloseDrawer={onCloseDrawer}
      visible={visible}
      footer={FooterContent}
      width={'80%'}
    >
      <LoadingView isShow={detailEvent.IsLoading} />
      <EventModal event={[event]} enable={true} successCalback={onEventSuccess} errorCalback={clearEvent} />
      <Row justify="space-between" gutter={[16, 8]}>
        <Col md={24} xs={24}>
          <AppSelect
            name="pagename"
            label="เลือกหน้าสร้าง Content"
            placeHolder="เลือกหน้าสร้าง Content"
            isRequired={isRequired('pagename')}
            isError={isError('pagename')}
            value={formData?.pagename}
            onChange={val => {
              onUpdateData(val, 'pagename')
              setCurrentPageName(val)
            }}
            menu={getMenuSelect(pagenamelist, 'pagename', 'pagename')}
            allowClear={false}
          />
        </Col>
        {/* <Col md={12} xs={24}></Col> */}
        <Col md={24} xs={24}>
          <BoxTitle>Header</BoxTitle>
          <ContentForm
            formData={formData.header}
            onUpdate={(val: ContentValue) => onUpdateData(val, 'header')}
            accessToken={currentUser?.accessToken}
            detail={detail?.sections?.find((item: ContentDetailSection) => item.section_name === 'Header')}
            maxChar={appConfig.contentMaxCharHeader}
            visible={visible}
          />
        </Col>
        <Col md={24} xs={24}>
          <BoxTitle>Footer</BoxTitle>
          <ContentForm
            formData={formData.footer}
            onUpdate={(val: ContentValue) => onUpdateData(val, 'footer')}
            accessToken={currentUser?.accessToken}
            detail={detail?.sections?.find((item: ContentDetailSection) => item.section_name === 'Footer')}
            maxChar={appConfig.contentMaxCharFooter}
            visible={visible}
          />
        </Col>
        <Col md={24} xs={24}>
          <SwitchMargin>
            <Switch checked={formData.status === 'active'} onChange={handleToggleStatus} />
            &nbsp;
            <LabelItem label={`สถานะ : ${formData.status === 'active' ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}`} />
          </SwitchMargin>
        </Col>
      </Row>
    </DetailDrawer>
  )
}

const SwitchMargin = styled.div`
  margin: 20px 0;
`

const CountTextWrapper = styled.div`
  width: 100%;
  text-align: right;
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
`

const BoxWrapper = styled.div`
  width: 100%;
  border-radius: 10px;
  padding: 15px 10px;
  //border: 1px solid ${(props: { theme: MyTheme }): string => props.theme.colors.whitebg};
  border: 1px solid #f0f0f0;
  margin-bottom: 10px;
  background-color:  ${(props: { theme: MyTheme }): string => props.theme.colors.white};
`

const BoxTitle = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body2};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
  margin-bottom: 10px;
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    event: selectContentActionEvent(state),
    detail: selectContentDetail(state),
    detailEvent: selectContentDetailEvent(state),
    configs: selectDatas(state),
    pagenamelist: selectPagenameList(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(ContentDetail)
